import { useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Biometric, BiometricPrice } from "@/lib/api_client";
import { useUpdateBiometric, useBiometricPrices, useCreateBiometricPrice, useUpdateBiometricPrice, useDeleteBiometricPrice } from "@/hooks/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface BasicInfoTabProps {
  biometric: Biometric;
}

export function BasicInfoTab({ biometric }: BasicInfoTabProps) {
  const updateBiometric = useUpdateBiometric();
  const { data: allPrices } = useBiometricPrices();
  const createPrice = useCreateBiometricPrice();
  const updatePrice = useUpdateBiometricPrice();
  const deletePrice = useDeleteBiometricPrice();

  const prices = (allPrices ?? []).filter((p) => p.biometricId === biometric.id);

  const [form, setForm] = useState({
    name: biometric.name,
    shortName: biometric.shortName ?? "",
    nlk: biometric.nlk ?? "",
    furstId: biometric.furstId ?? "",
    furstName: biometric.furstName ?? "",
    unit: biometric.unit ?? "",
    premiumOnly: biometric.premiumOnly ?? false,
    isBinary: biometric.isBinary ?? false,
  });

  const [localPrices, setLocalPrices] = useState<(BiometricPrice & { isNew?: boolean })[]>(
    prices.map((p) => ({ ...p }))
  );

  const handleSave = async () => {
    await updateBiometric.mutateAsync({
      id: biometric.id,
      data: {
        name: form.name,
        shortName: form.shortName || null,
        nlk: form.nlk || null,
        furstId: form.furstId.trim() || null,
        furstName: form.furstName || null,
        unit: form.unit || null,
        premiumOnly: form.premiumOnly,
        isBinary: form.isBinary,
      },
    });

    // Sync prices
    const existingIds = new Set(prices.map((p) => p.id));
    const localIds = new Set(localPrices.map((p) => p.id));

    for (const lp of localPrices) {
      if (existingIds.has(lp.id)) {
        await updatePrice.mutateAsync({ id: lp.id, data: { price: String(lp.price), source: String(lp.source) } });
      } else {
        await createPrice.mutateAsync({ biometricId: biometric.id, price: String(lp.price), source: String(lp.source) });
      }
    }
    for (const ep of prices) {
      if (!localIds.has(ep.id)) {
        await deletePrice.mutateAsync(ep.id);
      }
    }

    toast.success("Lagret");
  };

  const handleAddPrice = () => {
    setLocalPrices((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, biometricId: biometric.id, price: "0", source: "Furst", createdAt: "", updatedAt: "", isNew: true } as BiometricPrice & { isNew?: boolean },
    ]);
  };

  const handlePriceChange = (idx: number, field: string, value: string | number) => {
    setLocalPrices((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  const handleDeletePrice = (idx: number) => {
    setLocalPrices((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Grunnleggende informasjon</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Navn">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          <Field label="Beskrivelse">
            <Input
              value={form.shortName}
              onChange={(e) => setForm((f) => ({ ...f, shortName: e.target.value }))}
              placeholder="Kort beskrivelse"
            />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="NLK">
              <Input
                value={form.nlk}
                onChange={(e) => setForm((f) => ({ ...f, nlk: e.target.value }))}
              />
            </Field>
            <Field label="Furst ID">
              <Input
                value={form.furstId}
                onChange={(e) => setForm((f) => ({ ...f, furstId: e.target.value }))}
                placeholder="f.eks. 001"
              />
            </Field>
            <Field label="Furst Navn">
              <Input
                value={form.furstName}
                onChange={(e) => setForm((f) => ({ ...f, furstName: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Enhet">
            <Input
              value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              placeholder="f.eks. mmol/L"
            />
          </Field>
          <Field label="Premium Only">
            <Select
              value={form.premiumOnly ? "true" : "false"}
              onChange={(e) => setForm((f) => ({ ...f, premiumOnly: e.target.value === "true" }))}
              className="w-32"
            >
              <option value="false">Nei</option>
              <option value="true">Ja</option>
            </Select>
          </Field>
          <Field label="Positiv/negativ-markør">
            <Select
              value={form.isBinary ? "true" : "false"}
              onChange={(e) => setForm((f) => ({ ...f, isBinary: e.target.value === "true" }))}
              className="w-32"
            >
              <option value="false">Nei</option>
              <option value="true">Ja</option>
            </Select>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Priser</CardTitle>
          <Button variant="outline" size="sm" onClick={handleAddPrice}>
            <Plus className="h-3 w-3" />
            Legg til
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {localPrices.length === 0 && (
            <p className="text-sm text-admin-text-subtle">Ingen priser lagt til</p>
          )}
          {localPrices.map((price, idx) => (
            <div key={price.id} className="flex items-center gap-3">
              <Field label={idx === 0 ? "Pris (NOK)" : undefined} className="flex-1">
                <Input
                  type="number"
                  step="1"
                  value={Number(price.price).toFixed(2)}
                  onChange={(e) => {
                    const val = e.target.value.replace(",", ".");
                    handlePriceChange(idx, "price", parseFloat(val || "0"));
                  }}
                />
              </Field>
              <Field label={idx === 0 ? "Kilde" : undefined} className="w-32">
                <Input
                  value={String(price.source ?? "")}
                  onChange={(e) => handlePriceChange(idx, "source", e.target.value)}
                />
              </Field>
              <div className={idx === 0 ? "mt-6" : ""}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeletePrice(idx)}
                  className="text-admin-negative hover:text-admin-negative"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Separator />

      <Button onClick={handleSave} disabled={updateBiometric.isPending}>
        <Save className="h-4 w-4" />
        Lagre endringer
      </Button>
    </div>
  );
}

function Field({ label, children, className }: { label?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-admin-text-secondary mb-1">{label}</label>}
      {children}
    </div>
  );
}
